import { createReducer, on } from '@ngrx/store';

import { SearchActions } from '../actions/search.actions';
import { AuthActions } from '../actions/auth.actions';

import { DealerActions } from '../actions/dealer.actions';
import { SearchStoreUtils } from '../store-utils/search-store.utils';
import { DealerStoreUtils } from '../store-utils/dealer-store.utils';
import { CarBusinessLine } from 'src/app/public/car/constants/car-basic-data';
import { DealerCriteria } from 'src/app/public/account/models/dealer-criteria.model';
import { Dealer } from 'src/app/public/account/models/dealer.model';
import { CarBrief } from 'src/app/public/car/models/car-brief.model';
import { CarCriteria } from 'src/app/public/car/models/car-criteria.model';
import { Car } from 'src/app/public/car/models/car.model';
import { AppConstants } from 'src/app/public/common/constants/app-constants';
import { ObjectSource, GeneralBasicData } from 'src/app/public/common/constants/general-basic-data';
import { JobCriteria } from 'src/app/public/keep/models/job-criteria.model';
import { Job } from 'src/app/public/keep/models/job.model';


export interface SearchState {

    businessLine: CarBusinessLine;
    
    selectedCountryCodes: string[];

    searchCriteria: CarCriteria;
    dealerCriteria: DealerCriteria;
    jobCriteria: JobCriteria;

    searchedCarsPreview: any;
    searchedDealersPreview: any;
    searchedJobsPreview: any;

    searchedCars: Car[];
    searchedDealers: Dealer[];
    searchedJobs: Job[];

    sameSellerCarsToBuy: Car[];
    sameSellerCarsToRent: Car[];

    similarCarBriefs: CarBrief[];
    recentCarBriefs: CarBrief[];
    sameSellerCarBriefs: CarBrief[];
    
    currentCar: Car;
    currentDealer: Dealer;

    fetchingPreview: boolean;
    fetchingResults: boolean;
}


export const initialSearchData: SearchState = {

    businessLine : CarBusinessLine.BUY,

    selectedCountryCodes: [AppConstants.getSelectedCountryCodeDefault()],

    searchCriteria: new CarCriteria(),
    dealerCriteria: new DealerCriteria(),
    jobCriteria: new JobCriteria(),

    searchedCarsPreview: {},
    searchedDealersPreview: {},
    searchedJobsPreview: {},

    searchedCars: [],
    searchedDealers: [],
    searchedJobs: [],

    sameSellerCarsToBuy: [],
    sameSellerCarsToRent: [],

    similarCarBriefs: [],
    recentCarBriefs: [],
    sameSellerCarBriefs:[],

    currentCar: new Car(),
    currentDealer: new Dealer(),

    fetchingPreview: false,
    fetchingResults: false 
};

export const searchReducer = createReducer(
    initialSearchData,

    on( SearchActions.switchBusinessLine,  
        (state, action) => {
            return {
                ...state,
                businessLine : action.businessLine
            };
        }
    ),

    //Selected countries
    on( SearchActions.changeSelectedCountriesSubmit,  
        (state, action) => {
            return {
                ...state,
                selectedCountryCodes: action.countryCodes,
                //searchCriteria: Object.assign( state.searchCriteria, {countries: action.countryCodes} )
                searchCriteria: SearchStoreUtils.updateCountriesToCarCriteria(state.searchCriteria, action.countryCodes),
                dealerCriteria: SearchStoreUtils.updateCountriesToDealerCriteria(state.dealerCriteria, action.countryCodes),
                jobCriteria: SearchStoreUtils.updateCountriesToJobCriteria(state.jobCriteria, action.countryCodes),
            };
        }
    ),

    // preview submit
    on( SearchActions.searchPreviewCriteriaSubmit, 
        SearchActions.dealersPreviewCriteriaSubmit,  
        SearchActions.activitiesPreviewCriteriaSubmit,
        (state) => {
            return {
                ...state,
                fetchingPreview: true
            };
        }
    ),

    // results fetching true
    on( 
        SearchActions.fetchCarDetailsAndImagesSubmit,
        (state) => {
            return {
                ...state,
                fetchingResults: true
            };
        }
    ),


    // results fetching false
    on( 
        SearchActions.fetchCarDetailsSuccess,
        (state) => {
            return {
                ...state,
                fetchingResults: false
            };
        }
    ),

    //criteria submit
    on( SearchActions.searchCriteriaSubmit,  
        (state, action) => {
            return {
                ...state,
                fetchingResults: true,
                searchCriteria: action.carCriteria,
                selectedCountryCodes: action.carCriteria.countries
            };
        }
    ),

    on( SearchActions.dealerCriteriaSubmit,  
        (state, action) => {
            return {
                ...state,
                fetchingResults: true,
                dealerCriteria: action.dealerCriteria,
                selectedCountryCodes: action.dealerCriteria.countries
            };
        }
    ),

    on( SearchActions.jobCriteriaSubmit,  
        (state, action) => {
            return {
                ...state,
                fetchingResults: true,
                jobCriteria: action.jobCriteria,
                selectedCountryCodes: action.jobCriteria.countries
            };
        }
    ),

    // preview results
    on( SearchActions.searchPreviewResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedCarsPreview: action.carsPreview,
            fetchingPreview: false
        };
    }),

    on( SearchActions.dealersPreviewResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedDealersPreview: action.dealersPreview,
            fetchingPreview: false
        };
    }),

    on( SearchActions.activitiesPreviewResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedJobsPreview: action.activitiesPreview,
            fetchingPreview: false
        };
    }),


    // details results
    on( SearchActions.searchResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedCars: action.cars,
            fetchingResults: false
        };
    }),

    on( SearchActions.dealerResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedDealers: action.dealers,
            fetchingResults: false
        };
    }),

    on( SearchActions.jobResultsSuccess, (state, action) => {

        return {
            ...state, 
            searchedJobs: action.activities,
            fetchingResults: false
        };
    }),

    // reset search form
    on( SearchActions.resetCarCriteriaForm, (state, action) => {

        return {
            ...state, 
            searchCriteria: new CarCriteria(),
            fetchingResults: false
        };
    }),

    on( SearchActions.resetDealerCriteriaForm, (state, action) => {

        return {
            ...state, 
            dealerCriteria: new DealerCriteria(),
            fetchingResults: false
        };
    }),

    on( SearchActions.resetJobCriteriaForm, (state, action) => {

        return {
            ...state, 
            jobCriteria: new JobCriteria(),
            fetchingResults: false
        };
    }),


    // others
    on( SearchActions.fetchSameSellerCarsSuccess, (state, action) => {

        switch(action.businessLine.toString()) {
            case CarBusinessLine.BUY.toString(): {
                return {
                    ...state, 
                    sameSellerCarsToBuy: action.cars
                };
            }
    
            case CarBusinessLine.RENT.toString(): {
                return {
                    ...state, 
                    sameSellerCarsToRent: action.cars
                };
            }
            default: {
                return {
                    ...state
                };
            }
        }
    }),

    on( SearchActions.fetchCarDetailsSuccess, (state, action) => {

        return {
            ...state, 
            currentCar: action.car
        };
    }),

    on( SearchActions.fetchSimilarCarBriefsSuccess, (state, action) => {

        return {
            ...state, 
            similarCarBriefs: action.carBriefs
        };
    }),

    on( SearchActions.fetchRecentCarBriefsSuccess, (state, action) => {

        return {
            ...state, 
            recentCarBriefs: action.carBriefs
        };
    }),

    on( SearchActions.fetchSameSellerCarBriefsSuccess, (state, action) => {

        return {
            ...state, 
            sameSellerCarBriefs: action.carBriefs
        };
    }),

    on( DealerActions.fetchProviderPublicDetailsSuccess, (state, action) => {

        return {
            ...state, 
            currentDealer: action.provider
        };
    }),
/*
    on( DealerActions.addNewReviewSuccess, (state, action) => {

        return {
            ...state, 
            currentDealer: StoreUtils.updateReviews(state.currentDealer, action.ratingsSummary)
        };
    }),
*/
    on( DealerActions.reviewAddSuccess, (state, action) => {

        return {
            ...state, 
            //currentDealer: DealerStoreUtils.addReview(state.currentDealer, action.review)
        };
    }),

    on( DealerActions.reviewStatusUpdateSuccess, (state, action) => {

        return {
            ...state, 
            //currentDealer: DealerStoreUtils.updateReviewStatus(state.currentDealer, action.review)
        };
    }),

    on( DealerActions.reviewReplySuccess, (state, action) => {

        return {
            ...state, 
            currentDealer: DealerStoreUtils.addReviewReply(state.currentDealer, action.reviewId, action.reply)
        };
    }),

    on( DealerActions.reviewReplyStatusUpdateSuccess, (state, action) => {

        return {
            ...state, 
            //currentDealer: DealerStoreUtils.updateReviewReplyStatus(state.currentDealer, action.reviewId, action.replyIndex, action.reply)
        };
    }),

    on( DealerActions.toggleFavoriteSuccess, (state, action) => {

        return {
            ...state,
            //recentCarBriefs: DealerStoreUtils.toggleFavorite(state.recentCarBriefs, action.car.carId, action.makeFav),
            recentCarBriefs: DealerStoreUtils.toggleFavorite(state.recentCarBriefs, action.objectId, action.objectClass, action.makeFav),
            similarCarBriefs: DealerStoreUtils.toggleFavorite(state.similarCarBriefs, action.objectId, action.objectClass, action.makeFav),
            sameSellerCarBriefs: DealerStoreUtils.toggleFavorite(state.sameSellerCarBriefs, action.objectId, action.objectClass, action.makeFav),
            searchedCars: DealerStoreUtils.toggleFavorite(state.searchedCars, action.objectId, action.objectClass, action.makeFav),
            sameSellerCarsToBuy: DealerStoreUtils.toggleFavorite(state.sameSellerCarsToBuy, action.objectId, action.objectClass, action.makeFav)
            };
        
        }
    ),
    
    on( AuthActions.logoutSubmit, (state) => {
        //localStorage.removeItem("token");
        return {
            ...state,

            searchedCarsPreview: {},
            searchedDealersPreview: {},
            searchedJobsPreview: {},

            searchedCars: [],
            searchedDealers: [],
            searchedJobs: [],

            currentCar: new Car(),
            currentDealer: new Dealer(),
            fetchingPreview: false,
            fetchingResults: false
        };
    }),
    //on(reset, (state) => 0)


    on( DealerActions.contractUpsertSuccess, (state, action) => {
        return {
            ...state,
            searchedDealers: action.objectSource === ObjectSource.SEARCH ? SearchStoreUtils.upsertContractInDealers(state.searchedDealers, action.contract) : state.searchedDealers,
            };
        }
    ),

    on( DealerActions.contractDeleteSuccess, (state, action) => {
        return {
            ...state,
            searchedDealers:  action.objectSource === ObjectSource.SEARCH ? SearchStoreUtils.deleteContractInDealers(state.searchedDealers, action.contractId) : state.searchedDealers,
            };
        }
    ),
);


// selectors

export const selectSelectedCountryCodes = (state: SearchState) => state.selectedCountryCodes;
export const selectSelectedCountries = (state: SearchState) => GeneralBasicData.getCountriesByCodes(state.selectedCountryCodes)!;

export const selectAllSearchedCars = (state: SearchState) => state.searchedCars;
export const selectAllSearchedDealers = (state: SearchState) => state.searchedDealers;
export const selectAllSearchedJobs = (state: SearchState) => state.searchedJobs;

export const selectSameSellerCars = (state: SearchState, businessLine: CarBusinessLine) => {
    switch(businessLine.toString()) {
        case CarBusinessLine.BUY.toString(): {
            return state.sameSellerCarsToBuy;
        }

        case CarBusinessLine.RENT.toString(): {
            return state.sameSellerCarsToRent;
        }
        default: {
            return [];
        }
    }
        
}

export const selectCurrentCar = (state: SearchState) => state.currentCar;

export const selectSimilarCarBriefs = (state: SearchState) => state.similarCarBriefs;
export const selectRecentCarBriefs = (state: SearchState) => state.recentCarBriefs;
export const selectSameSellerCarBriefs = (state: SearchState) => state.sameSellerCarBriefs;

export const selectCurrentDealer = (state: SearchState) => state.currentDealer;
export const selectAllSearchedCarsCount = (state: SearchState) => state.searchedCars.length;

export const selectAllSearchedCarsPreviewTotalCount = (state: SearchState) => (state.searchedCarsPreview.totalCount === undefined ? 0 : state.searchedCarsPreview.totalCount);
export const selectAllSearchedDealersPreviewTotalCount = (state: SearchState) => (state.searchedDealersPreview.totalCount === undefined ? 0 : state.searchedDealersPreview.totalCount);
export const selectAllSearchedJobsPreviewTotalCount = (state: SearchState) => (state.searchedJobsPreview.totalCount === undefined ? 0 : state.searchedJobsPreview.totalCount);

export const selectSearchFetchingPreview = (state: SearchState) => state.fetchingPreview;
export const selectSearchFetchingResults = (state: SearchState) => state.fetchingResults;

